/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/* CPO Gallery Functionality */
(() => {
  const root = document;

  const openLightbox = (dialog, imgEl, url) => {
    imgEl.src = url;
    if (!dialog.open) dialog.showModal();
  };

  root.addEventListener("click", (e) => {
    const section = e.target.closest(".CpoTwoCol");
    if (!section) return;

    const featuredBtn = e.target.closest("[data-cpo-lightbox-open]");
    const thumbBtn = e.target.closest("[data-cpo-thumb]");
    const dialog = section.querySelector("[data-cpo-lightbox]");
    const dialogImg = section.querySelector("[data-cpo-lightbox-img]");
    const closeBtn = e.target.closest("[data-cpo-lightbox-close]");

    if (!dialog || !dialogImg) return;

    if (closeBtn) {
      dialog.close();
      dialogImg.src = "";
      return;
    }

    if (featuredBtn) {
      openLightbox(dialog, dialogImg, featuredBtn.getAttribute("data-full"));
      return;
    }

    if (thumbBtn) {
      const full = thumbBtn.getAttribute("data-full");

      // Swap featured image
      const featuredImage = section.querySelector(".CpoMedia__FeaturedImage");
      const featuredButton = section.querySelector(
        ".CpoMedia__FeaturedButton"
      );

      if (featuredImage && featuredButton) {
        featuredImage.src = full;
        featuredButton.setAttribute("data-full", full);
      }

      // Enlarge on click (per your request)
      openLightbox(dialog, dialogImg, full);
    }
  });

  root.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;

    document.querySelectorAll("[data-cpo-lightbox]").forEach((dialog) => {
      if (dialog.open) {
        const img = dialog.querySelector("[data-cpo-lightbox-img]");
        dialog.close();
        if (img) img.src = "";
      }
    });
  });
})();








/* Variant Dynamic Cart Changes */
/**
 * Preorder / restock button updater for Focal theme.
 *
 * product:rerender does NOT fire on this theme — Focal updates the DOM
 * via targeted morphing, not a full fragment swap. We can safely update
 * the button directly on variant:changed.
 *
 * formId in the event detail is a DOM element reference (not a string),
 * so we scope the container lookup via e.target (the form element) instead.
 */
(function () {
  function buildDateLabel(mode, dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = d.getUTCDate();
    const month = d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
    const year = d.getUTCFullYear();

    if (mode === 'pre_order') {
      if (day <= 10) return `Early-${month} ${year}`;
      if (day <= 20) return `Mid-${month} ${year}`;
      return `Late-${month} ${year}`;
    }
    if (mode === 'restock') {
      return `${month} ${day}, ${year}`;
    }
    return '';
  }

  function resolveMetafields(productData, variantId) {
    const variantData = productData.variants[variantId] || {};
    return {
      preorder_mode:
        variantData.preorder_mode || productData.product.preorder_mode || '',
      ship_date:
        variantData.ship_date || productData.product.ship_date || '',
    };
  }

  function applyUpdate(container, variant) {
    const productId = container.dataset.productId;
    const productData = window.__preorderData?.[productId];

    if (!productData) {
      console.warn('[preorder] No __preorderData for productId:', productId);
      return;
    }

    const { preorder_mode, ship_date } = resolveMetafields(
      productData,
      variant.id
    );

    // Select ALL buttons, not just the first
    const btns = container.querySelectorAll('[data-product-add-to-cart-button]');
    const shipDateEl = container.querySelector('.product-form__ship-date');
    const shipDateLabel = container.querySelector(
      '.product-form__ship-date-label'
    );

    if (!btns.length) {
      console.warn('[preorder] Could not find button in container');
      return;
    }

    let btnText;
    if (!variant.available) {
      btnText = window.theme?.strings?.soldOut || 'Restocking soon';
    } else if (preorder_mode === 'pre_order') {
      btnText = window.theme?.strings?.preOrder || 'Pre-Order';
    } else if (preorder_mode === 'restock') {
      btnText = window.theme?.strings?.restock || 'Restocking Soon – Reserve Yours Now';
    } else {
      btnText = window.theme?.strings?.addToCart || 'Add to Cart';
    }

    // Update ALL buttons
    btns.forEach((btn) => {
      btn.querySelector('.loader-button__text').textContent = btnText;
      btn.dataset.buttonContent = btnText;

      btn.classList.remove('button--primary', 'button--secondary', 'button--ternary');
      if (!variant.available) {
        btn.classList.add('button--ternary');
        btn.setAttribute('disabled', '');
      } else {
        btn.removeAttribute('disabled');
        const hasDynamicPaymentButton =
          container.querySelector('.shopify-payment-button') !== null;
        btn.classList.add(
          hasDynamicPaymentButton ? 'button--secondary' : 'button--primary'
        );
      }
    });

    if (shipDateEl && shipDateLabel) {
      const dateLabel = buildDateLabel(preorder_mode, ship_date);
      if (preorder_mode && dateLabel) {
        shipDateLabel.textContent = dateLabel;
        shipDateEl.style.display = '';
      } else {
        shipDateEl.style.display = 'none';
      }
    }

    const paymentBtn = container.querySelector('.shopify-payment-button');
    if (paymentBtn) {
      paymentBtn.style.display = variant.available ? '' : 'none';
    }
  }

function init() {
  // Store the last known variant so the observer can reference it
  let pendingVariant = null;
  let pendingForm = null;
  let observerTimer = null;

  function runUpdate() {
    if (!pendingVariant || !pendingForm) return;

    const section =
      pendingForm.closest('[id^="shopify-section-"]') ||
      pendingForm.closest('.shopify-section') ||
      document;

    const containers = section.querySelectorAll(
      'product-payment-container[data-product-id], product-payment-container2'
    );

    if (!containers.length) {
      console.warn('[preorder] No payment container found in section');
      return;
    }

    containers.forEach((container) => {
      if (!container.dataset.productId) {
        const productIdInput = pendingForm.querySelector('[name="product-id"]');
        if (productIdInput) {
          container.dataset.productId = productIdInput.value;
        }
      }
      applyUpdate(container, pendingVariant);
    });
  }

  function watchForDpoUpdate(form, variant) {
    pendingVariant = variant;
    pendingForm = form;

    const section =
      form.closest('[id^="shopify-section-"]') ||
      form.closest('.shopify-section') ||
      document;

    const containers = section.querySelectorAll(
      'product-payment-container[data-product-id], product-payment-container2'
    );

    if (!containers.length) {
      console.warn('[preorder] No payment container found to observe');
      return;
    }

    // Run once immediately in case DPO already finished
    runUpdate();

    // Also observe each container for DOM changes made by DPO
    containers.forEach((container) => {
      const observer = new MutationObserver(() => {
        // Debounce — wait for DPO to finish all its mutations
        clearTimeout(observerTimer);
        observerTimer = setTimeout(() => {
          observer.disconnect();
          runUpdate();
        }, 50);
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });

      // Safety disconnect after 3 seconds regardless
      setTimeout(() => observer.disconnect(), 3000);
    });
  }

  document.addEventListener('variant:changed', (e) => {
    const { variant } = e.detail || {};
    if (!variant) return;
    console.log('[preorder] variant:changed fired', variant.id);
    watchForDpoUpdate(e.target, variant);
  });
}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();